import { useCallback, useState } from "react";
import { enqueueSnackbar } from "notistack";
import { hapticError, hapticSuccess } from "../../../lib/haptics";
import { claimPushSoftAsk } from "../../../lib/push";
import { alertsErrorCopy, alertsOnToast } from "../screenAlertCopy";
import { useSetScreenAlerts } from "./useScreenAlerts";

/**
 * Switching a screen's alerts on or off, with the house feedback: success or
 * error haptics, the truthful status when they come on (or why they could
 * not), and, on native, the one-time push soft ask after a first alert.
 * `lead` starts the toast (the save dialog's "Saved as …").
 */
export const useScreenAlertToggle = () => {
  const setAlerts = useSetScreenAlerts();
  const [softAskOpen, setSoftAskOpen] = useState(false);
  const { mutate } = setAlerts;

  const toggle = useCallback(
    (screenId: string, enabled: boolean, lead?: string) => {
      const withLead = (message: string) =>
        lead ? `${lead} ${message}` : message;
      mutate(
        { id: screenId, enabled },
        {
          onSuccess: (result) => {
            hapticSuccess();
            if (!enabled) return;
            enqueueSnackbar(withLead(alertsOnToast(result)), {
              variant: "success",
              autoHideDuration: 4000,
            });
            void claimPushSoftAsk().then((claimed) => {
              if (claimed) setSoftAskOpen(true);
            });
          },
          onError: (error) => {
            hapticError();
            enqueueSnackbar(withLead(alertsErrorCopy(error)), {
              variant: lead ? "info" : "error",
              autoHideDuration: 6000,
            });
          },
        },
      );
    },
    [mutate],
  );

  return {
    toggle,
    isPending: setAlerts.isPending,
    softAskOpen,
    closeSoftAsk: () => setSoftAskOpen(false),
  };
};
